import { showNotification } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { customAxios } from "lib/axios";

export type ProjectPayload = {
  name: string;
  description: string;
  category: string;
  clientId: string;
  projectOverview: string;
  projectObjectives: string;
  scopeOfWork: string;
  exclusions: string;
  inclusions: string;
  assumptions: string;
  constraints: string;
  stakeholdersInfo: string;
  timelineMilestones: string;
  successCriteria: string;
  risksMitigation: string;
  changeManagementProcess: string;
  approvals: string;
};

async function updateProject(
  projectId: string,
  userId: string,
  payload: ProjectPayload
): Promise<ProjectPayload> {
  const url = `/projects/${projectId}?userId=${userId}`;
  const { data } = await customAxios.patch<ProjectPayload>(url, payload);
  return data;
}

export function useUpdateProject(orgId?: string) {
  const queryClient = useQueryClient();

  return useMutation(
    ({
      projectId,
      userId,
      payload,
    }: {
      projectId: string;
      userId: string;
      payload: ProjectPayload;
    }) => updateProject(projectId, userId, payload),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(["projects", variables.projectId], data);

        showNotification({
          title: "Project Updated",
          message: `Successfully updated`,
          color: "teal",
        });
      },
      onError: (error: AxiosError) => {
        showNotification({
          title: "Update Failed",
          message:
            error.message || "Could not update project. Please try again.",
          color: "red",
        });
      },
    }
  );
}
